/**
 * Reward Detail Page
 * Displays full details of a single reward instance
 */

import { useState } from 'react'
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Divider,
  Grid,
  Avatar,
} from '@mui/material'
import {
  ArrowBack,
  QrCode2,
  Send,
  CardGiftcard,
  Schedule,
  CalendarMonth,
  Repeat,
  Groups,
  Explore,
} from '@mui/icons-material'
import { format } from 'date-fns'
import { RewardInstanceStatus, RewardSourceType, getSourceTypeName } from '@api/types/rewards'
import { useRewardInstance, useCanTransferReward } from '../hooks/useRewardInstance'
import { RewardStatusChip } from '../components/RewardStatusChip'
import { RewardContentDisplay } from '../components/RewardContentDisplay'
import { QRCodeModal } from '../components/QRCodeModal'
import { TransferModal } from '../components/TransferModal'
import {
  formatExpirationDate,
  formatRemainingUses,
  getExpirationWarningLevel,
} from '../utils/rewardHelpers'

export default function RewardDetailPage() {
  const { instanceId } = useParams<{ instanceId: string }>()
  const navigate = useNavigate()
  const [showQRModal, setShowQRModal] = useState(false)
  const [showTransferModal, setShowTransferModal] = useState(false)

  const { data: reward, isLoading, error } = useRewardInstance(instanceId)
  const { data: canTransfer } = useCanTransferReward(instanceId)

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !reward) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/rewards')}
          sx={{ mb: 2 }}
        >
          Back to Rewards
        </Button>
        <Alert severity="error">
          Failed to load reward details. The reward may not exist or you may not have access.
        </Alert>
      </Box>
    )
  }

  const isActive = reward.status === RewardInstanceStatus.Active
  const expirationWarning = getExpirationWarningLevel(reward.expiresAt)

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/rewards" underline="hover" color="inherit">
          My Rewards
        </Link>
        <Typography color="text.primary">{reward.template.title || 'Reward'}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: isActive ? 'primary.light' : 'action.hover',
              color: isActive ? 'primary.contrastText' : 'text.secondary',
            }}
          >
            <CardGiftcard sx={{ fontSize: 40 }} />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {reward.template.title || 'Untitled Reward'}
              </Typography>
              <RewardStatusChip status={reward.status} />
            </Box>
            {reward.template.description && (
              <Typography variant="body1" color="text.secondary">
                {reward.template.description}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isActive && (
              <Button
                variant="contained"
                startIcon={<QrCode2 />}
                onClick={() => setShowQRModal(true)}
              >
                Show QR Code
              </Button>
            )}
            {canTransfer?.canTransfer && reward.template.isTransferable && (
              <Button
                variant="outlined"
                startIcon={<Send />}
                onClick={() => setShowTransferModal(true)}
              >
                Transfer
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Main content */}
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom>
              Reward Content
            </Typography>
            <RewardContentDisplay
              reward={reward}
              showQRAction={isActive ? () => setShowQRModal(true) : undefined}
            />
          </Paper>
        </Grid>

        {/* Metadata sidebar */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom>
              Details
            </Typography>

            {/* Remaining uses */}
            {reward.remainingUses !== null && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Repeat color="action" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Remaining Uses
                  </Typography>
                  <Typography variant="body1">
                    {formatRemainingUses(reward.remainingUses, reward.template.usesPerInstance)}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Expiration */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Schedule
                color={
                  expirationWarning === 'very-soon' || expirationWarning === 'expired'
                    ? 'error'
                    : expirationWarning === 'soon'
                      ? 'warning'
                      : 'action'
                }
              />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Expiration
                </Typography>
                <Typography
                  variant="body1"
                  color={
                    expirationWarning === 'very-soon' || expirationWarning === 'expired'
                      ? 'error.main'
                      : expirationWarning === 'soon'
                        ? 'warning.main'
                        : 'text.primary'
                  }
                >
                  {formatExpirationDate(reward.expiresAt)}
                </Typography>
              </Box>
            </Box>

            {/* Claimed date */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CalendarMonth color="action" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Claimed
                </Typography>
                <Typography variant="body1">
                  {format(new Date(reward.claimedAt), 'MMM d, yyyy')}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Source info */}
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Source
            </Typography>

            {/* Group Source */}
            {reward.sourceType === RewardSourceType.Group && reward.source?.groupId && (
              <Box
                component={RouterLink}
                to={`/groups/${reward.source.groupId}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 2,
                  textDecoration: 'none',
                  color: 'inherit',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                <Avatar
                  src={reward.source.groupThumbnail ? `https://images.hadv.quest/${reward.source.groupThumbnail}` : undefined}
                  variant="rounded"
                  sx={{ width: 40, height: 40, borderRadius: 1, flexShrink: 0 }}
                >
                  <Groups />
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {reward.source.groupName || 'Group'}
                  </Typography>
                  {reward.source.groupDescription && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {reward.source.groupDescription}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* Adventure Source */}
            {reward.sourceType === RewardSourceType.Adventure && reward.source?.adventureId && (
              <Box
                component={RouterLink}
                to={`/adventures/${reward.source.adventureId}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 2,
                  textDecoration: 'none',
                  color: 'inherit',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                <Avatar
                  src={reward.source.adventureThumbnail ? `https://images.hadv.quest/${reward.source.adventureThumbnail}` : undefined}
                  variant="rounded"
                  sx={{ width: 40, height: 40, borderRadius: 1, flexShrink: 0 }}
                >
                  <Explore />
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {reward.source.adventureTitle || 'Adventure'}
                  </Typography>
                  {reward.source.adventureTypeName && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {reward.source.adventureTypeName}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* Fallback for other source types or missing source details */}
            {(reward.sourceType === RewardSourceType.Manual ||
              reward.sourceType === RewardSourceType.Transfer ||
              (!reward.source?.groupId && !reward.source?.adventureId)) && (
              <Typography variant="body1" sx={{ mb: 2 }}>
                {getSourceTypeName(reward.sourceType)}
              </Typography>
            )}

            {/* Transfer count */}
            {reward.transferCount > 0 && (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Transfer History
                </Typography>
                <Typography variant="body1">
                  Transferred {reward.transferCount} time{reward.transferCount !== 1 ? 's' : ''}
                </Typography>
                {reward.originalRecipientDisplayName &&
                  reward.originalRecipientDisplayName !== reward.currentOwnerDisplayName && (
                    <Typography variant="caption" color="text.secondary">
                      Originally received by {reward.originalRecipientDisplayName}
                    </Typography>
                  )}
              </>
            )}

            {/* Post-expiration note */}
            {reward.isExpired && (reward as any).allowPostExpirationRedemption && (
              <Alert severity="info" sx={{ mt: 2 }}>
                This reward allows post-expiration redemption. The business may honor it at their
                discretion.
              </Alert>
            )}

            {/* Can't transfer reason */}
            {reward.template.isTransferable && canTransfer && !canTransfer.canTransfer && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {canTransfer.reason || 'This reward cannot be transferred at this time.'}
              </Alert>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* QR Code Modal */}
      <QRCodeModal
        open={showQRModal}
        onClose={() => setShowQRModal(false)}
        reward={reward}
      />

      {/* Transfer Modal */}
      <TransferModal
        open={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        rewardId={reward.id}
        rewardTitle={reward.template.title || 'Untitled Reward'}
        onSuccess={() => {
          setShowTransferModal(false)
          navigate('/rewards')
        }}
      />
    </Box>
  )
}
